import { HttpClient, HttpHeaders, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Customer, ENVIRONMENT, Environment } from '@netfoundry-ui/shared/model';
import { result, toLower } from 'lodash';
import { catchError, delay } from 'rxjs/operators';
import { throwError as observableThrowError } from 'rxjs/internal/observable/throwError';
import {
    GetOption,
    HateoasResourceOperation,
    HttpMethod,
    PagedGetOption,
    PagedResourceCollection,
} from '@lagoshny/ngx-hateoas-client';
import { take, tap } from 'rxjs/operators';
import { HTTP_CLIENT, TokenService } from './token.service';
import { LoggerService } from './logger.service';


@Injectable({ providedIn: 'root' })
export class CustomerService extends HateoasResourceOperation<Customer> {
    apiUrl;
    appsUrl;
    networkPricing = 199.0;
    regionPricing = [{ region: null, price: 0.2, rate: 'GB' }];
    lastPageCount = 0;
    lastTotalCount = 0;
    static defaultHttpAccept = {
        headers: { accept: 'application/hal+json' },
        pageParams: {
            page: 0,
            size: 50,
        },
    };

    constructor(
        private logger: LoggerService,
        private tokenService: TokenService,
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {
        super(Customer);
        this.apiUrl = this.environment.customerURL;
        this.appsUrl = this.environment.appsURL;
    }

    public findCustomers(options?: PagedGetOption): Promise<Customer[]> {
        return super
            .getPage({ ...CustomerService.defaultHttpAccept, ...options })
            .pipe(
                tap((val) => {
                    this.lastPageCount = val.totalPages;
                    this.lastTotalCount = val.totalElements;
                })
            )
            .toPromise()
            .then((customers) => (customers ? customers.resources : []));
    }

    // Looks up specific customers by id, in one request (the endpoint's `id` filter takes a set).
    // Used for role display: a role points at a single customer, so paging through every customer the
    // caller can read - potentially all of them for an NF-internal super-user - adds no value.
    public findCustomersByIds(ids: string[]): Promise<Customer[]> {
        const wantedIds = (ids || []).filter((id) => !!id);
        if (!wantedIds.length) {
            return Promise.resolve([] as Customer[]);
        }
        const headers = new HttpHeaders().set('Accept', 'application/hal+json');
        const params = new HttpParams()
            .set('id', wantedIds.join(','))
            .set('page', '0')
            .set('size', `${wantedIds.length}`);
        return this.http
            .get(`${this.apiUrl}customers`, { headers, params })
            .toPromise()
            .then((result: any) => this.extractCustomers(result))
            .catch((error: HttpErrorResponse) => {
                this.logger.error(`Customer request failed with code ${error?.status}`, error?.error);
                return [] as Customer[];
            });
    }

    findCustomersProfiles() {
        const headers = new HttpHeaders().set('Accept', 'application/json').set('nf-validate', '');
        return this.http
            .get(this.apiUrl + `customers?enabledForOnboarding=true`, {
                headers: headers,
                responseType: 'json',
            })
            .toPromise();
    }

    // the customer list is a HAL collection with the items under _embedded.customerList
    private extractCustomers(result: any): Customer[] {
        if (!result) {
            return [];
        }
        if (Array.isArray(result)) {
            return result as Customer[];
        }
        const embedded = result._embedded;
        if (embedded) {
            if (Array.isArray(embedded.customerList)) {
                return embedded.customerList as Customer[];
            }
            const collection = Object.values(embedded).find((value) => Array.isArray(value));
            if (collection) {
                return collection as Customer[];
            }
        }
        return [];
    }

    public findCustomer(customerId: string) {
        const fullpath = `${this.apiUrl}customers/${customerId}`;
        return this.http.get(fullpath);
    }

    public getSelfCustomer(organizationId: string) {
        const headers = new HttpHeaders().set('Accept', 'application/json').set('nf-validate', '');
        return this.http
            .get(
                this.apiUrl +
                    `customers?customerResourceAssignments.resourceId=${organizationId}&customerResourceAssignments.resourceType=Organization`,
                {
                    headers: headers,
                    responseType: 'json',
                }
            )
            .toPromise();
    }

    public getCustomer(customerId: string) {
        const fullpath = `${this.apiUrl}customers/${customerId}`;
        const headers = new HttpHeaders().set('Accept', 'application/hal+json');
        return this.http.get(fullpath, {
            headers: headers
        });
    }

    public getCustomerAssignments(customerId: string) {
        const fullpath = `${this.apiUrl}customer-resource-assignments`;
        const params = new HttpParams().set('customerId', customerId).set('resourceType', 'features');
        const headers = new HttpHeaders().set('Accept', 'application/json');
        return this.http.get(fullpath, {
            headers: headers,
            params: params,
        });
    }

    public getNetworkGroupLimits(customerId: string) {
        const fullpath = `${this.apiUrl}entitlements/${customerId}`;
        const headers = new HttpHeaders().set('Accept', 'application/json');
        return this.http.get(fullpath, {
            headers: headers,
        });
    }

    public onboard(model, parentCustomerId) {
        const fullpath = `${this.apiUrl}/customers/${parentCustomerId}/onboard`;
        return this.http.post(fullpath, model);
    }

    public offboard(parentCustomerId, id) {
        const fullpath = `${this.apiUrl}/customers/${parentCustomerId}/offboard/${id}`;
        return this.http.put(fullpath, {});
    }

    public getUsageReport(id: string, startAt: string, endAt: string) {
        const headers = new HttpHeaders().set('Accept', 'text/csv');
        const fullpath = `${this.apiUrl}/customers/${id}/usage`;
        const params = {
            startAt,
            endAt,
        };
        return this.http.get(fullpath, { params });
    }

    public validateOnboard(parentCustomerId, model) {
        const headers = new HttpHeaders().set('Accept', 'application/json').set('nf-validate', '');
        return this.http
            .post(this.apiUrl + `customers/${parentCustomerId}/onboard`, model, {
                headers: headers,
                responseType: 'json',
            })
            .toPromise();
    }

    public addFeatureExistingCustomer(id: string, feature: any) {
        const headers = this.setHeaders();
        const fullpath = `${this.apiUrl}/customers/${id}/provision-feature`;
        return this.http.post(fullpath, feature, { headers: headers });
    }

    public removeFeatureExistingCustomer(id: string, feature: any) {
        const headers = this.setHeaders();
        const fullpath = `${this.apiUrl}/customers/${id}/remove-feature`;
        const body = {
            resourceType: feature,
        };
        return this.http.delete(fullpath, { headers: headers, body: body });
    }

    /**
     * Updates the maximum number of networks for a customer's network group(s).
     * maxNetworkCount must be -1 (unlimited) or a positive number. When networkGroupId is omitted the
     * change is applied to every network group assigned to the customer.
     */
    public updateEntitlement(id: string, maxNetworkCount: number, networkGroupId?: string) {
        const headers = this.setHeaders();
        const fullpath = `${this.apiUrl}entitlements/${id}`;
        const body: any = { maxNetworkCount };
        if (networkGroupId) {
            body.networkGroupId = networkGroupId;
        }
        return this.http.patch(fullpath, body, { headers: headers });
    }

    public enabledProfiles(parentCustomerId) {
        const headers = new HttpHeaders().set('Accept', 'application/json').set('nf-validate', '');
        return this.http
            .get(`https://` + this.environment.gatewayUrl + `/customer-profiles/`, {
                headers: headers,
                responseType: 'json',
            })
            .toPromise();
    }

    public update(id, model) {
        const headers = this.setHeaders();
        const fullpath = `${this.apiUrl}/customers/${id}`;
        return this.http.patch(fullpath, model, { headers: headers });
    }

    public delete(id: string) {
        const headers = this.setHeaders();
        const fullpath = `${this.apiUrl}/customers/${id}`;
        return this.http.delete(fullpath, { headers: headers });
    }

    selfResume() {
        const path = `customers/self/resume`;
        return this.put(path, {})
            .toPromise()
            .then((result: any) => {
                this.logger.log(result);
                return result;
            });
    }

    submitHubspotEntries(entries: any = []) {
        const fullpath = `${this.appsUrl}survey`;

        const body = {};
        entries.forEach((entry: any) => {
            body[entry.key] = entry.value;
        });
        return this.http.post(fullpath, JSON.stringify(body), { headers: this.setHeaders() });
    }

    requestSelfOffboard() {
        const path = '/customers/self/offboard';
        return this.post(path, {})
            .toPromise()
            .then((result: any) => {
                this.logger.log(result);
                return result;
            });
    }

    getCountriesList() {
        const path = 'countries';
        return this.get(path)
            .toPromise()
            .then((result: any) => {
                this.logger.log(result);
                return result;
            });
    }

    setNewSelfServiceUserFlags(tenantLabel, zipcode = '', country = '', plan = '') {
        localStorage.setItem(`nf_new_self_service_${tenantLabel}`, 'true');
        localStorage.setItem(`showGettingStarted_${tenantLabel}`, 'true');
        localStorage.setItem(`showSurvey_${tenantLabel}`, 'true');
        localStorage.setItem(`createNetwork_${tenantLabel}`, 'true');
        localStorage.setItem(`createFabric_${tenantLabel}`, 'true');
        localStorage.setItem(`nf_show_quick_start_hint_${tenantLabel}`, 'true');
        localStorage.setItem(`show_endpoints_info_${tenantLabel}`, 'true');
        localStorage.setItem(`show_services_info_${tenantLabel}`, 'true');
        localStorage.setItem(`show_edge-routers_info_${tenantLabel}`, 'true');
        localStorage.setItem(`show_appwans_info_${tenantLabel}`, 'true');
        localStorage.setItem(`show_posture-checks_info_${tenantLabel}`, 'true');
        localStorage.setItem(`show_edge-router-policies_info_${tenantLabel}`, 'true');
        localStorage.setItem(`show_certificates_info_${tenantLabel}`, 'true');
        localStorage.setItem(`fabricZipcode_${tenantLabel}`, zipcode);
        localStorage.setItem(`fabricCountry_${tenantLabel}`, country);
        localStorage.removeItem(`newInvitedUser_${tenantLabel}`);
        if (toLower(plan) === 'teams') {
            localStorage.setItem(`isSelfServiceTeams_${tenantLabel}`, 'true');
        }
    }

    /**
     * @param path
     * @param data
     */
    public post(path: string, data) {
        const fullpath = `${this.apiUrl}${path}`;
        const body = data;

        return this.http.post(fullpath, JSON.stringify(body), { headers: this.setHeaders() });
    }

    /**
     * @param path
     * @param data
     */
    public put(path: string, data) {
        const fullpath = `${this.apiUrl}${path}`;
        const body = data;

        return this.http.put(fullpath, JSON.stringify(body), { headers: this.setHeaders() });
    }

    /**
     * Generic GETter
     */

    public get(path: string) {
        const fullpath = `${this.apiUrl}${path}`;

        return this.http.get(fullpath, { headers: this.setHeaders() });
    }

    /**
     * Return headers
     */
    private setHeaders(): HttpHeaders {
        const headers = new HttpHeaders();

        return headers.append('Content-Type', 'application/json');
    }

    /**
     * Generic error handler for bad requests
     */
    protected handleError(scope: any, error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            // A client-side or network error occurred. Handle it accordingly.
            scope.logger.error('Feature Service error occurred:', error.error);
        } else {
            // The backend returned an unsuccessful response code.
            // The response body may contain clues as to what went wrong,
            scope.logger.error(`Feature Backend returned code ${error.status}`, error.error);
        }
        scope.lastErrorSource.next(error);
        return observableThrowError(error);
    }
}
